import React, {useMemo, useState} from 'react';
import SGHeadline from 'components/Typography/SGHeadline';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import filterOptions from './constants/FilterOptions';
import getFilterOptions from './functions/getFilterOptions';
import FiltersTab from './FiltersTab';
import SGBody from 'components/Typography/SGBody';
import SearchInput from './SearchInput';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';
import {useHistory, useLocation} from 'react-router-dom';
import SiteListTable from './SiteListTable';
import Container from 'components/Container';
import {useSiteState} from 'hooks/useSiteInfo/SiteStateProvider';

const StyledSpacer = styled.div`
  height: 60px;
`;

const StyledTableSpacer = styled.div`
  height: 48px;
`;

const StyledBody = styled(SGBody)`
  text-align: center;
  max-width: 745px;
`;

const StyledGrid = styled(Grid)`
  display: flex;
  justify-content: center;
`;

function useQuery() {
  const {search} = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function Landing() {
  //#region State and Memoized Values
  const [open, setOpen] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});

  const {
    state: {isSiteStateReady, sites},
  } = useSiteState();

  const history = useHistory();

  let query = useQuery();

  const allSuggestions = useMemo(() => {
    if (!isSiteStateReady) {
      return [];
    }
    return sites.filter(site => site.siteType === 'Retail');
  }, [sites, isSiteStateReady]);

  const filterFilledOptions = useMemo(() => {
    if (!isSiteStateReady) return filterOptions;
    return getFilterOptions(allSuggestions);
  }, [isSiteStateReady, allSuggestions]);
  //#endregion

  const viewAllQuery: string | null = query.get('viewAll');

  //#region Event Handlers
  const handleClick = () => {
    setOpen(!open);
    handleFilterClear();
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    setSelectedFilters(prevFilters => {
      if (prevFilters[filterKey] !== undefined && prevFilters[filterKey] === value) {
        // if value is already selected, remove it
        const {[filterKey]: value, ...rest} = {...prevFilters};
        return rest;
      } else {
        return {
          ...prevFilters,
          [filterKey]: value,
        };
      }
    });
  };

  const handleFilterClear = () => {
    setSelectedFilters({});
  };

  const handleSearchClick = (value: Site) => {
    history.push(`/npos?siteId=${value.siteId}`);
  };

  //#endregion

  return (
    <>
      {viewAllQuery === null && (
        <FiltersTab
          isOpen={open}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterChange}
          filterFilledOptions={filterFilledOptions}
          handleFilterClear={handleFilterClear}
          handleButtonChange={handleClick}
          isDisabled={!isSiteStateReady}
        />
      )}

      <Container>
        <Grid container justify="center" spacing={2}>
          {viewAllQuery === null && (
            <>
              <Grid item xs={12}>
                <StyledSpacer />
              </Grid>
              <StyledGrid item xs={12}>
                <SGHeadline variant={'h4'} className={'text-color'}>
                  Store search for Diagnostics
                </SGHeadline>
              </StyledGrid>
              <StyledGrid item xs={12}>
                <StyledBody>
                  Begin by searching for a store. Enter the store name or number to view NPOS
                  features to view detailed diagnostic data. You can also use our filters to search
                  by store brand, status, and location.
                </StyledBody>
              </StyledGrid>
              <StyledGrid item xs={12}>
                <SearchInput
                  isFilterActive={open}
                  clickHandler={handleSearchClick}
                  filterOptions={selectedFilters}
                  searchSuggestions={allSuggestions}
                  allDataIsLoaded={isSiteStateReady}
                  filterClickedHandler={handleClick}
                />
              </StyledGrid>
            </>
          )}
          {viewAllQuery !== null && (
            <Grid item xs={12}>
              <StyledTableSpacer />
              <SiteListTable
                sites={allSuggestions}
                viewAllQuery={viewAllQuery}
                selectedFilters={selectedFilters}
                handleFilterClear={handleFilterClear}
                handleFilterChange={handleFilterChange}
                filterFilledOptions={filterFilledOptions}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
}
